/**
 * Brevo Conversations helper.
 *
 * The widget script is no longer auto-loaded from index.html — it now ships
 * behind cookie consent. `initBrevoConversations()` is called from App.js
 * (or when the user enables the "chat" category) to inject the loader.
 *
 * `openChat()` is the single call site components use to surface the widget.
 * If the visitor hasn't consented to chat yet, clicking a chat CTA pops the
 * cookie preferences modal so they can opt in.
 */
import { hasConsent, openPreferences, setConsent, getConsent } from "@/lib/consent";

const BREVO_ID = "6a2de9d43c68e605ce01709f";
const SCRIPT_SRC = "https://conversations-widget.brevo.com/brevo-conversations.js";
let injected = false;

/**
 * Inject the Brevo Conversations loader. Idempotent — safe to call multiple
 * times. The widget script itself decides when to render; we only ensure the
 * <script> tag is in the DOM and the global queue is initialized.
 */
export function initBrevoConversations() {
  if (typeof window === "undefined" || injected) return;
  if (typeof window.BrevoConversations !== "function") {
    window.BrevoConversationsID = BREVO_ID;
    window.BrevoConversations = function () {
      (window.BrevoConversations.q = window.BrevoConversations.q || []).push(arguments);
    };
  }
  const s = document.createElement("script");
  s.async = true;
  s.src = SCRIPT_SRC;
  if (document.head) document.head.appendChild(s);
  injected = true;
}

export function openChat({ context, email, name } = {}) {
  if (typeof window === "undefined") return;

  // Visitor hasn't opted into chat yet — surface the preferences modal so they
  // can enable it deliberately. We treat clicking a chat CTA as a strong
  // intent signal: flip chat ON automatically while leaving analytics alone,
  // then queue the openChat call once the widget is up.
  if (!hasConsent("chat")) {
    const current = getConsent();
    setConsent({ analytics: current.analytics, chat: true });
    initBrevoConversations();
    // Brevo's loader is async; retry the open call once the global is ready.
    let attempts = 0;
    const tryOpen = () => {
      attempts += 1;
      if (typeof window.BrevoConversations === "function" && window.BrevoConversations.q !== undefined) {
        // Queue interface is ready — push openChat + context.
        return doOpen({ context, email, name });
      }
      if (attempts < 40) setTimeout(tryOpen, 100);
    };
    tryOpen();
    // Also nudge them to the preferences modal so they know what just happened.
    openPreferences();
    return;
  }

  if (typeof window.BrevoConversations !== "function") {
    return; // Widget not loaded yet — silently no-op
  }

  doOpen({ context, email, name });
}

function doOpen({ context, email, name }) {
  try {
    const data = {};
    if (email) data.email = email;
    if (name) {
      const [first, ...rest] = name.split(" ");
      data.firstName = first;
      if (rest.length) data.lastName = rest.join(" ");
    }
    if (context) {
      data.notes = `Triggered from: ${context}`;
    }
    if (Object.keys(data).length) {
      window.BrevoConversations("updateIntegrationData", data);
    }
    window.BrevoConversations("openChat", true);
  } catch (_err) {
    /* Widget present but in an invalid state. Silent-fail — never throw. */
  }
}
